import { z } from "zod";
import { competitionDayField } from "./competition/common";
import { MEET_CURSOR_PATTERN, type MeetEventSummary, type MeetSportSummary, type MeetVenueSummary } from "./competition/meets";
import type { Medal, MeetOutcome, PhaseType, ResultStatus, ScheduleStatus } from "./competition/vocab";
import { contingentQuery } from "./contingent";
import { PAGE_SIZE_MAX } from "./pagination";

/**
 * Sector 4 · the Contingent Dashboard's competition tabs (user, 2026-11-02;
 * after SUKMA's Contingent Management Module: Schedule, Medals, Record
 * Breaker, Venues). Read-only, ONE delegation (games × organisation), chosen
 * like the dashboard's (a NOC its own, ADMIN / SUPER_ADMIN pick; another
 * organisation → 404). The competition data is every OVR reader's; the one
 * card datum — a PHOTO — is the delegation's own people only, each through
 * `cardInScope` (another NOC's entrant gets a flag, never a photo).
 *
 *   GET /contingent/schedule?…&day&sport_id&medal_only&when&chance&cursor&limit   the NOC's meets (keyset)
 *   GET /contingent/schedule/days?…&sport_id&medal_only&when&chance               the day chips (same WHERE)
 *   GET /contingent/chances?…                                                      medal chances still to come, counted
 *   GET /contingent/venues?…&page&page_size                                        venues the NOC competes at
 *   GET /contingent/medals?…&sport_id&page&page_size                               the NOC's medals, with the winners
 *   GET /contingent/medallists?…&min_medals&page&page_size                         the NOC's medallists
 *
 * The medal tally (`GET /medals/tally`) and the record marks (`GET
 * /records/marks?responsible_organization_id=`) are the competition's own
 * reads; a meet's pop-up is `GET /meets/:id`.
 */

/** The page's tabs; `readiness` is the original dashboard. */
export const CONTINGENT_TABS = ["readiness", "schedule", "medals", "records", "venues"] as const;
export type ContingentTab = (typeof CONTINGENT_TABS)[number];

/**
 * Which meets, by where they stand:
 *   upcoming  no result yet (NONE / START_LIST) and not CANCELLED
 *   live      result LIVE
 *   finished  result UNOFFICIAL or OFFICIAL
 */
export const CONTINGENT_SCHEDULE_WHEN = ["all", "upcoming", "live", "finished"] as const;
export type ContingentScheduleWhen = (typeof CONTINGENT_SCHEDULE_WHEN)[number];

/**
 * A meet's medal chance, by its phase in a medal event (SUKMA's "Expected
 * medal"), decided in SQL (`chanceSql`):
 *   gold_match    a FINAL that decides medals between two entries
 *   bronze_match  a BRONZE phase that decides medals
 *   semifinal     a SEMIFINAL of a medal event (it decides who goes to the gold match;
 *                 in a combat sport its losers take bronze)
 *   medal_final   any other meet that decides medals (a race final, a judged final)
 * Everything else has none. A `chance` filter keeps meets still to come:
 * no result yet or LIVE, not CANCELLED.
 */
export const CONTINGENT_CHANCES = ["gold_match", "bronze_match", "medal_final", "semifinal"] as const;
export type ContingentChance = (typeof CONTINGENT_CHANCES)[number];
/** `chance=any` = every chance. */
export const CONTINGENT_CHANCE_FILTERS = ["any", ...CONTINGENT_CHANCES] as const;
export type ContingentChanceFilter = (typeof CONTINGENT_CHANCE_FILTERS)[number];

export const CONTINGENT_SCHEDULE_PAGE_SIZE_DEFAULT = 25;
/** Day chips: a games is a few weeks; more than this is test data, clamped (`/medals/daily`'s rule). */
export const CONTINGENT_SCHEDULE_DAYS_MAX = 62;
export const CONTINGENT_LIST_PAGE_SIZE_DEFAULT = 25;
/** `min_medals`: 1 = every medallist, 2 = SUKMA's "multi-medallist". */
export const CONTINGENT_MEDALLISTS_MIN_MAX = 10;

const uuid = z.string().uuid().toLowerCase();
const booleanQueryParam = z.enum(["true", "false"]).transform((v) => v === "true");

/** The filters the schedule and its day chips share — so a chip's count is that day's list total. */
const scheduleFilterShape = {
	sport_id: uuid.optional(),
	medal_only: booleanQueryParam.optional(),
	when: z.enum(CONTINGENT_SCHEDULE_WHEN).default("all"),
	chance: z.enum(CONTINGENT_CHANCE_FILTERS).optional(),
};

/** `GET /contingent/schedule/days`. */
export const contingentScheduleDaysQuery = contingentQuery.extend(scheduleFilterShape);
export type ContingentScheduleDaysQueryInput = z.input<typeof contingentScheduleDaysQuery>;

/**
 * `GET /contingent/schedule` — the NOC's top-level meets that have a time,
 * keyset on (scheduled_start, meet_id) ascending. `day` is a day in the
 * GAMES' time zone. The cursor is `GET /meets`' shape: send `next_cursor` back as received.
 */
export const contingentScheduleQuery = contingentQuery.extend({
	...scheduleFilterShape,
	day: competitionDayField.optional(),
	cursor: z.string().max(100).regex(MEET_CURSOR_PATTERN, "Malformed cursor").optional(),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(CONTINGENT_SCHEDULE_PAGE_SIZE_DEFAULT),
});
export type ContingentScheduleQueryInput = z.input<typeof contingentScheduleQuery>;

const listPage = {
	page: z.coerce.number().int().min(1).default(1),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(CONTINGENT_LIST_PAGE_SIZE_DEFAULT),
};

/** `GET /contingent/venues` — offset pages: a games has tens of venues. */
export const contingentVenuesQuery = contingentQuery.extend(listPage);
export type ContingentVenuesQueryInput = z.input<typeof contingentVenuesQuery>;

/** `GET /contingent/medallists` — offset pages, ordered gold, silver, bronze (descending), then name. */
export const contingentMedallistsQuery = contingentQuery.extend({
	...listPage,
	min_medals: z.coerce.number().int().min(1).max(CONTINGENT_MEDALLISTS_MIN_MAX).default(1),
});
export type ContingentMedallistsQueryInput = z.input<typeof contingentMedallistsQuery>;

/** `GET /contingent/medals` — offset pages in programme order (discipline, event), then colour. A NOC wins tens of medals per sport. */
export const contingentMedalsQuery = contingentQuery.extend({
	...listPage,
	sport_id: uuid.optional(),
});
export type ContingentMedalsQueryInput = z.input<typeof contingentMedalsQuery>;

/** One day chip. Counts are of the NOC's meets that day, after the filters. */
export interface ContingentScheduleDay {
	/** YYYY-MM-DD in the games' time zone. */
	date: string;
	meets: number;
	medal_meets: number;
	live: number;
	finished: number;
}

export interface ContingentScheduleDays {
	/** The games' zone, and today in it — the chip to open first. */
	timezone: string;
	today: string;
	days: ContingentScheduleDay[];
	/** The NOC's meets with no time yet (not on any day). */
	unscheduled: number;
}

/** `GET /contingent/chances` — the medal-chance tiles: meets still to come per chance (ONE statement, the schedule's `chance` rule). */
export interface ContingentChances {
	counts: Array<{ chance: ContingentChance; count: number }>;
	/** Of them, LIVE now. */
	live: number;
	/** The NOC's athletes in those meets (a team counts each member). */
	athletes: number;
}

/** One entry of a meet: the NOC's own, or (head-to-head only) the opponent. */
export interface ContingentEntrant {
	competitor_id: string;
	name: string;
	/** The organisation's IOC code (MAS); null when it has no country. */
	noc_code: string | null;
	flag_url: string | null;
	/** An individual of OUR delegation: their accreditation photo (short-lived URL). Teams and opponents: null. */
	photo_url: string | null;
	team: boolean;
	rank: number | null;
	result_display: string | null;
	irm: string | null;
	outcome: MeetOutcome | null;
	records: string[];
	/** The medal this entry won, on a meet that decides medals. */
	medal: Medal | null;
}

/** One row of the NOC's schedule. */
export interface ContingentMeet {
	meet_id: string;
	meet_no: string;
	name: string | null;
	scheduled_start: string;
	schedule_status: ScheduleStatus;
	result_status: ResultStatus;
	/** Its phase awards medals and its event is a medal event (`medal_events`' rule). */
	decides_medals: boolean;
	chance: ContingentChance | null;
	sport: MeetSportSummary & { icon_url: string | null };
	discipline: { discipline_id: string; code: string; name: string };
	event: MeetEventSummary;
	phase: { phase_id: string; type: PhaseType; name: string };
	venue: MeetVenueSummary | null;
	/** Filled slots. */
	field_size: number;
	/** The NOC's own entries in this meet. */
	ours: ContingentEntrant[];
	/** The other side of a head-to-head (field_size ≤ 2); empty for a field. */
	opponents: ContingentEntrant[];
}

export interface ContingentSchedulePage {
	items: ContingentMeet[];
	next_cursor: string | null;
	/** First page only (null on later pages). */
	total: number | null;
}

export interface ContingentSportRef {
	sport_id: string;
	code: string;
	name: string;
	icon_url: string | null;
}

/** A venue the NOC competes at. */
export interface ContingentVenue {
	venue_id: string;
	code: string;
	name: string;
	city: string | null;
	parent_name: string | null;
	cluster: string | null;
	/** The NOC's meets here: all, and those with a result. */
	meets: number;
	finished: number;
	/** The next of them still to come (not CANCELLED, no result); null when none. */
	next_at: string | null;
	sports: ContingentSportRef[];
}

/** A person on a medal-winning roster of OUR delegation; `photo_url` as `ContingentEntrant`'s. */
export interface ContingentWinner {
	people_id: string;
	name: string;
	photo_url: string | null;
}

/** One of the NOC's medals (`GET /contingent/medals`). */
export interface ContingentMedal {
	event_medal_id: string;
	medal: Medal;
	awarded_at: string;
	sport: ContingentSportRef;
	discipline: { discipline_id: string; code: string; name: string };
	event: { event_id: string; code: string; name: string; gender: MeetEventSummary["gender"] };
	/** The entry's display name (the athlete, the pair, or the team). */
	name: string;
	team: boolean;
	/** The roster but reserves, in roster order. */
	winners: ContingentWinner[];
}

/** One medal of a medallist. */
export interface ContingentMedallistMedal {
	medal: Medal;
	awarded_at: string;
	team: boolean;
	sport: { sport_id: string; code: string; name: string };
	event: { event_id: string; code: string; name: string };
}

/** A person of the NOC on a roster that won a medal (reserves are not counted). */
export interface ContingentMedallist {
	people_id: string;
	name: string;
	photo_url: string | null;
	gold: number;
	silver: number;
	bronze: number;
	total: number;
	medals: ContingentMedallistMedal[];
}
